import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress, Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  getVariantInfo: (variantId: string) => { productTitle: string; variantTitle: string; image?: string; tags?: string } | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function EditTreatModal({ open, onClose, sub, getVariantInfo, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const variantId = sub?.shopify_variant_id || sub?.external_variant_id || "";
  const info = variantId ? getVariantInfo(String(variantId)) : null;

  useEffect(() => {
    if (open && sub) {
      setQuantity(sub.quantity || 1);
      setError(null);
    }
  }, [open, sub]);

  const handleUpdateQuantity = async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/update-quantity", {
        method: "POST",
        body: { subscriptionId: sub.id, quantity },
      });
      if (!res.ok) throw new Error(res.error || "Failed to update quantity");
      onSuccess(`Treat quantity updated to ${quantity}`);
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to update quantity");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!sub) return;
    setRemoving(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/cancel", {
        method: "POST",
        body: {
          subscriptionId: sub.id,
          cancellationReason: "Admin removed treat",
          cancellationFeedback: "Removed via admin dev tools",
        },
      });
      if (!res.ok) throw new Error(res.error || "Failed to remove treat");
      onSuccess("Treat removed successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to remove treat");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.brownDeep }}>Edit Treat</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          {info?.image && (
            <Box
              component="img"
              src={info.image}
              alt={info.productTitle}
              sx={{ width: 60, height: 60, borderRadius: "8px", objectFit: "cover" }}
            />
          )}
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brownDeep }}>
              {info?.productTitle || sub?.product_title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
              {info?.variantTitle || sub?.variant_title} &bull; £{sub?.price}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.brown, mb: 1 }}>Quantity</Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mb: 2 }}>
          <IconButton
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
            disabled={quantity <= 1}
            sx={{ border: `1px solid ${COLORS.borderLight}` }}
          >
            <RemoveIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: 24, minWidth: 40, textAlign: "center" }}>
            {quantity}
          </Typography>
          <IconButton
            onClick={() => setQuantity(q => Math.min(99, q + 1))}
            disabled={quantity >= 99}
            sx={{ border: `1px solid ${COLORS.borderLight}` }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        <Button
          variant="outlined"
          color="error"
          fullWidth
          startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          onClick={handleRemove}
          disabled={removing || loading}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Remove Treat
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpdateQuantity}
          disabled={loading || quantity === (sub?.quantity || 1)}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Update Quantity"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
